import { useEffect, useContext, useMemo, useState } from "react";
import axios from "axios";

import HeaderBar from "../../components/HeaderBar";
import PayRollLayoutWrapper from "../../components/PayRollLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";

import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import {
  Building2,
  Search,
  RefreshCcw,
  Printer,
  ChevronDown,
  ChevronUp,
  Building,
} from "lucide-react";

const BASE_URL = "https://nissi-india.com"; // change if needed

const ResignedBonusStaff = () => {
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const primaryBtnClass = `bg-${baseColor}-${baseShade}`;
  const primaryHoverClass = `hover:bg-${baseColor}-700`;

  const finYearId = localStorage.getItem("finYearId") || "";
  const loginBranchId = localStorage.getItem("branchId") || "";
  const branchType = localStorage.getItem("branchType") || "HO";
  const fyr = localStorage.getItem("fyr") || ""; // "2023-2024"

  const [branches, setBranches] = useState([]);
  const [canViewAll, setCanViewAll] = useState(false);

  const [showFilter, setShowFilter] = useState(true);
  const [branchId, setBranchId] = useState("Select");
  const [branchLocked, setBranchLocked] = useState(false);

  const [fromYear, setFromYear] = useState("");
  const [toYear, setToYear] = useState("");

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  // pagination
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const screenBadge = useMemo(() => "INFRA SCREEN : (Resigned Bonus Staff)", []);

  const inputBase =
    `w-full px-4 py-3 rounded-2xl border outline-none transition ` +
    (isDark
      ? "bg-gray-800/40 border-white/10 text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500"
      : "bg-white border-gray-200 text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-blue-500");

  const labelBase = `text-sm font-semibold ${isDark ? "text-gray-200" : "text-gray-700"}`;

  const cardBase =
    `rounded-3xl border shadow-xl overflow-hidden ` +
    (isDark
      ? "border-white/10 bg-gradient-to-br from-white/15 to-white/5 backdrop-blur-md"
      : "border-gray-200 bg-white");

  const primaryBtn =
    `inline-flex items-center justify-center gap-2 px-6 py-3 rounded-2xl text-white font-bold shadow-lg ` +
    `hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${primaryBtnClass} ${primaryHoverClass}`;

  const ghostBtn =
    `inline-flex items-center justify-center gap-2 px-6 py-3 rounded-2xl font-bold border transition-all duration-200 ` +
    (isDark
      ? "border-white/10 text-white hover:bg-white/10"
      : "border-gray-200 text-gray-800 hover:bg-gray-50");

  const warnBtn =
    `inline-flex items-center justify-center gap-2 px-6 py-3 rounded-2xl font-bold border transition-all duration-200 ` +
    (isDark
      ? "border-amber-400/30 text-amber-200 hover:bg-amber-500/10"
      : "border-amber-200 text-amber-800 hover:bg-amber-50");

  function splitFY(raw) {
    const v = (raw ?? "").toString().trim();
    if (!v) return { from: "", to: "" };
    const parts = v.split("-").map((x) => x.trim()).filter(Boolean);
    if (parts.length >= 2) return { from: parts[0], to: parts[1] };
    return { from: "", to: "" };
  }

  const loadMeta = async () => {
    if (!String(finYearId).trim()) {
      toast.warn("Please select Financial Year.");
      return;
    }
    try {
      setLoading(true);
      const res = await axios.get(`${BASE_URL}/api/bonus-resigned-staff/meta`, {
        params: {
          finYearId: Number(finYearId),
          branchId: Number(loginBranchId || 0),
          branchType: branchType || "",
          fyr: fyr || "",
        },
      });

      if (!res?.data?.success) {
        toast.error(res?.data?.message || "Meta load failed");
        return;
      }

      const data = res.data;

      setBranches(data.branches || []);
      setCanViewAll(!!data.canViewAll);

      // FY
      let fy = { from: data.fromYear || "", to: data.toYear || "" };
      if (!fy.from || !fy.to) {
        const local = splitFY(fyr);
        fy = { from: local.from, to: local.to };
      }
      setFromYear(fy.from);
      setToYear(fy.to);

      // Branch default/lock
      const isHO = (data.branchType || branchType) === "HO";
      if (isHO) {
        setBranchLocked(false);
        setBranchId(String(data.defaultBranchId || loginBranchId || "Select"));
      } else {
        setBranchLocked(true);
        setBranchId(String(data.defaultBranchId || loginBranchId || "Select"));
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  const fetchList = async (p = page) => {
    if (!String(finYearId).trim()) {
      toast.warn("Please select Financial Year.");
      return;
    }
    if (!fromYear || !toYear) {
      toast.warn("Financial year not found (from/to year missing).");
      return;
    }

    const isSelect = String(branchId) === "Select" || !String(branchId).trim();
    if (isSelect && !canViewAll) {
      toast.warn("Please Select Branch..");
      return;
    }

    try {
      setLoading(true);
      const res = await axios.get(`${BASE_URL}/api/bonus-resigned-staff/data`, {
        params: {
          finYearId: Number(finYearId),
          branchId: isSelect ? "Select" : String(branchId),
          fromYear: String(fromYear),
          toYear: String(toYear),
          page: p,
          pageSize,
        },
      });

      if (res?.data?.success) {
        setRows(res.data.rows || []);
        setTotal(res.data.total || 0);
        setTotalPages(res.data.totalPages || 1);
        setPage(res.data.page || p);
      } else {
        toast.error(res?.data?.message || "Failed to load list.");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadMeta();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load first page once filter is ready
  useEffect(() => {
    if (!String(finYearId).trim()) return;
    if (!fromYear || !toYear) return;
    if (!branchId) return;

    setPage(1);
    fetchList(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [finYearId, fromYear, toYear, branchId]);

  // Page change fetch (guarded to prevent FY missing toast)
  useEffect(() => {
    if (!String(finYearId).trim()) return;
    if (!fromYear || !toYear) return;
    if (!branchId) return;

    fetchList(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, finYearId, fromYear, toYear, branchId]);

  const handleSearch = async (e) => {
    e?.preventDefault?.();
    setPage(1);
    await fetchList(1);
  };

  const handleViewAll = async () => {
    setBranchId("Select");
    setPage(1);
    await fetchList(1);
  };

  const handlePrint = () => {
    if (!String(finYearId).trim()) {
      toast.warn("Please select Financial Year.");
      return;
    }
    if (!fromYear || !toYear) {
      toast.warn("Financial year not found (from/to year missing).");
      return;
    }
    const isSelect = String(branchId) === "Select" || !String(branchId).trim();
    if (isSelect && !canViewAll) {
      toast.warn("Please Select Branch..");
      return;
    }

    const url =
      `/PayRoll/BonusResignedStaffPrint?finYearId=${encodeURIComponent(finYearId)}` +
      `&branchId=${encodeURIComponent(isSelect ? "Select" : String(branchId))}` +
      `&fromYear=${encodeURIComponent(fromYear)}` +
      `&toYear=${encodeURIComponent(toYear)}`;

    window.open(url, "_blank", "noopener,noreferrer");
  };

  // fixed columns (match SP output)
  const monthKeys = useMemo(
    () => [
      "October",
      "November",
      "December",
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
    ],
    []
  );

  const num = (v) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
  };

  const totals = useMemo(() => {
    const t = {
      October: 0,
      November: 0,
      December: 0,
      January: 0,
      February: 0,
      March: 0,
      April: 0,
      May: 0,
      June: 0,
      July: 0,
      August: 0,
      September: 0,
      Total: 0,
      Batta: 0,
      "Batta Bonus": 0,
      "Total Bonus": 0,
    };

    (rows || []).forEach((r) => {
      monthKeys.forEach((k) => (t[k] += num(r?.[k])));
      t.Total += num(r?.Total);
      t.Batta += num(r?.Batta);
      t["Batta Bonus"] += num(r?.["Batta Bonus"]);
      t["Total Bonus"] += num(r?.["Total Bonus"]);
    });

    return t;
  }, [rows, monthKeys]);

  const show0 = (v) => {
    if (v === null || v === undefined || v === "") return "0";
    return String(v);
  };

  const showingFrom = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const showingTo = Math.min(page * pageSize, total);

  return (
    <PayRollLayoutWrapper>
      <ToastContainer
        position="top-right"
        autoClose={2800}
        hideProgressBar={false}
        closeOnClick
        pauseOnHover
        theme={isDark ? "dark" : "light"}
      />

      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">BONUS RESIGNED STAFF</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div
          className={`mx-auto max-w-7xl p-6 mt-6 rounded-3xl shadow-2xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : `${currentSkin.glass} bg-white text-black border-gray-200`
          }`}
        >
          {/* Top row */}
          <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-4 mb-6">
            <div className="flex flex-wrap items-center gap-4">
              <div
                className={`w-12 h-12 rounded-2xl flex items-center justify-center shadow ${
                  isDark ? "bg-white/10" : "bg-gray-100"
                }`}
              >
                <Building2 className={isDark ? "text-white" : "text-gray-700"} />
              </div>

              <div>
                <div className={`text-2xl font-extrabold ${isDark ? "text-white" : "text-gray-900"}`}>
                  Resigned Employees Bonus Master
                </div>
                <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                  Branch-wise listing • FY: {fromYear && toYear ? `${fromYear}-${toYear}` : "-"}
                </div>
              </div>

              <div
                className={`px-4 py-2 rounded-2xl text-sm font-bold ${
                  isDark ? "bg-blue-900/40 text-blue-100" : "bg-blue-100 text-blue-800"
                }`}
              >
                {screenBadge}
              </div>

              <div
                className={`px-4 py-2 rounded-2xl text-sm font-bold ${
                  isDark ? "bg-emerald-900/40 text-emerald-100" : "bg-emerald-100 text-emerald-800"
                }`}
              >
                {total} Records
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-3 justify-end">
              <button onClick={() => fetchList(page)} className={ghostBtn} disabled={loading}>
                <RefreshCcw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
                Refresh
              </button>

              {canViewAll && (
                <button onClick={handleViewAll} className={warnBtn} disabled={loading}>
                  <RefreshCcw className="w-4 h-4" />
                  View All
                </button>
              )}

              <button onClick={handlePrint} className={primaryBtn} disabled={loading}>
                <Printer className="w-4 h-4" />
                Print
              </button>

              <button onClick={() => setShowFilter((v) => !v)} className={ghostBtn}>
                {showFilter ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                {showFilter ? "Hide Filter" : "Show Filter"}
              </button>
            </div>
          </div>

          {/* Filter */}
          {showFilter && (
            <form onSubmit={handleSearch} className={cardBase + " p-6 mb-6"}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label className={labelBase}>
                    Site / Branch <span className="text-red-500">*</span>
                  </label>
                  <div className="mt-2 flex items-center gap-2">
                    <Building className={`w-5 h-5 ${isDark ? "text-gray-300" : "text-gray-500"}`} />
                    <select
                      className={inputBase}
                      value={branchId}
                      onChange={(e) => {
                        setPage(1);
                        setBranchId(e.target.value);
                      }}
                      disabled={loading || branchLocked}
                      title={branchLocked ? "Branch locked for non-HO user" : ""}
                    >
                      <option value="Select">Select</option>
                      {branches.map((b) => (
                        <option key={b.fld_branchid} value={String(b.fld_branchid)}>
                          {b.fld_branchname}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="flex items-end">
                  <button type="submit" className={primaryBtn} disabled={loading}>
                    <Search className="w-4 h-4" />
                    Search
                  </button>
                </div>
              </div>
            </form>
          )}

          {/* Table */}
          <div className={cardBase}>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className={`${primaryBtnClass} text-white`}>
                  <tr>
                    <th className="px-4 py-4 text-center font-bold whitespace-nowrap">S.No</th>
                    <th className="px-4 py-4 text-center font-bold whitespace-nowrap">Code</th>
                    <th className="px-4 py-4 text-center font-bold whitespace-nowrap">Employee Name</th>
                    <th className="px-4 py-4 text-center font-bold whitespace-nowrap">Designation</th>
                    <th className="px-4 py-4 text-center font-bold whitespace-nowrap">DOJ</th>
                    <th className="px-4 py-4 text-center font-bold whitespace-nowrap">A/C No.</th>

                    {monthKeys.map((m) => (
                      <th key={m} className="px-4 py-4 text-center font-bold whitespace-nowrap">
                        {m.slice(0, 3)}
                      </th>
                    ))}

                    <th className="px-4 py-4 text-center font-bold whitespace-nowrap">Total</th>
                    <th className="px-4 py-4 text-center font-bold whitespace-nowrap">Bonus %</th>
                    <th className="px-4 py-4 text-center font-bold whitespace-nowrap">Batta</th>
                    <th className="px-4 py-4 text-center font-bold whitespace-nowrap">Batta Bonus</th>
                    <th className="px-4 py-4 text-center font-bold whitespace-nowrap">Total Bonus</th>
                    <th className="px-4 py-4 text-center font-bold whitespace-nowrap">Location</th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {loading ? (
                    Array.from({ length: pageSize }).map((_, idx) => (
                      <tr key={idx} className={isDark ? "bg-gray-900" : "bg-white"}>
                        {Array.from({ length: 6 + monthKeys.length + 6 }).map((__, c) => (
                          <td key={c} className="px-4 py-4">
                            <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded animate-pulse" />
                          </td>
                        ))}
                      </tr>
                    ))
                  ) : rows.length === 0 ? (
                    <tr>
                      <td
                        colSpan={6 + monthKeys.length + 6}
                        className={`px-4 py-12 text-center ${isDark ? "text-gray-300" : "text-gray-700"}`}
                      >
                        No Records Found
                      </td>
                    </tr>
                  ) : (
                    rows.map((r, idx) => (
                      <tr
                        key={idx}
                        className={`${isDark ? "bg-gray-900" : "bg-white"} hover:${
                          isDark ? "bg-gray-800" : "bg-gray-50"
                        }`}
                      >
                        <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {(page - 1) * pageSize + idx + 1}
                        </td>

                        <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {r?.EmployeeCode ?? ""}
                        </td>

                        <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {r?.["Employee Name"] ?? ""}
                        </td>

                        <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {r?.Designation ?? ""}
                        </td>

                        <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {r?.DOJ ?? ""}
                        </td>

                        <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {r?.["A/C No."] ?? ""}
                        </td>

                        {monthKeys.map((m) => (
                          <td
                            key={m}
                            className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}
                          >
                            {show0(r?.[m])}
                          </td>
                        ))}

                        <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {show0(r?.Total)}
                        </td>

                        <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {show0(r?.fld_bonuspercentage)}
                        </td>

                        <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {show0(r?.Batta)}
                        </td>

                        <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {show0(r?.["Batta Bonus"])}
                        </td>

                        <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {show0(r?.["Total Bonus"])}
                        </td>

                        <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {r?.Location ?? ""}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>

                {!loading && rows.length > 0 && (
                  <tfoot>
                    <tr className={isDark ? "bg-gray-800/40" : "bg-gray-50"}>
                      <td className={`px-4 py-4 text-center font-extrabold ${isDark ? "text-white" : "text-gray-900"}`}>
                        Total
                      </td>

                      {/* Code..A/C No empty */}
                      <td />
                      <td />
                      <td />
                      <td />
                      <td />

                      {monthKeys.map((m) => (
                        <td
                          key={m}
                          className={`px-4 py-4 text-center font-extrabold ${isDark ? "text-white" : "text-gray-900"}`}
                        >
                          {totals[m].toFixed(2)}
                        </td>
                      ))}

                      <td className={`px-4 py-4 text-center font-extrabold ${isDark ? "text-white" : "text-gray-900"}`}>
                        {totals.Total.toFixed(2)}
                      </td>

                      {/* Bonus % - do not total */}
                      <td />

                      <td className={`px-4 py-4 text-center font-extrabold ${isDark ? "text-white" : "text-gray-900"}`}>
                        {totals.Batta.toFixed(2)}
                      </td>

                      <td className={`px-4 py-4 text-center font-extrabold ${isDark ? "text-white" : "text-gray-900"}`}>
                        {totals["Batta Bonus"].toFixed(2)}
                      </td>

                      <td className={`px-4 py-4 text-center font-extrabold ${isDark ? "text-white" : "text-gray-900"}`}>
                        {totals["Total Bonus"].toFixed(2)}
                      </td>

                      <td />
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          </div>

          {/* Bottom row: pagination */}
          <div className="flex flex-col md:flex-row items-center justify-between mt-6 gap-4">
            <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
              Showing <b>{showingFrom}</b> to <b>{showingTo}</b> of <b>{total}</b>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              {totalPages > 1 && (
                <div className="inline-flex rounded-2xl overflow-hidden border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900">
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className={`px-4 py-2 text-sm font-bold ${
                      page === 1
                        ? "text-gray-400 cursor-not-allowed"
                        : "hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-800 dark:text-gray-200"
                    }`}
                  >
                    Prev
                  </button>

                  {[...Array(totalPages)].map((_, i) => (
                    <button
                      key={i}
                      onClick={() => setPage(i + 1)}
                      className={`px-4 py-2 text-sm font-bold transition ${
                        page === i + 1
                          ? `${primaryBtnClass} text-white`
                          : "hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-800 dark:text-gray-200"
                      }`}
                    >
                      {i + 1}
                    </button>
                  ))}

                  <button
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    className={`px-4 py-2 text-sm font-bold ${
                      page === totalPages
                        ? "text-gray-400 cursor-not-allowed"
                        : "hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-800 dark:text-gray-200"
                    }`}
                  >
                    Next
                  </button>
                </div>
              )}
            </div>
          </div>

          <div className={`mt-4 text-xs ${isDark ? "text-gray-400" : "text-gray-500"}`}>
            Note: totals are calculated for the current page only (pagination). Print page will show totals for all rows.
          </div>
        </div>
      </div>
    </PayRollLayoutWrapper>
  );
};

export default ResignedBonusStaff;
